import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AlertService } from '../../../../../shared/alert/alert.service';
import { NgbActiveModal, NgbDate, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ParamContext } from '../../../../../shared/service/param.context';
import { HelpService } from '../../../../../shared/service/help.service';
import { BreedingMethodFilterTypeEnum, BreedingMethodsSelectComponent } from '../../../../../shared/breeding-methods-select/breeding-methods-select.component';
import { PreviewCrossesModel } from '../../../../../shared/study/model/preview-crosses.model';
import { finalize } from 'rxjs/internal/operators/finalize';
import { GermplasmListCreationComponent } from '../../../../../shared/list-creation/germplasm-list-creation.component';
import { GermplasmListEntry } from '../../../../../shared/list-creation/model/germplasm-list';
import { CrossNamePresetSetting, CrossNameSetting, SaveCrossesRequestModel } from '../../../../../shared/study/model/save-crosses.request.model';
import { DateHelperService } from '../../../../../shared/service/date.helper.service';
import { GermplasmService } from '../../../../../shared/germplasm/service/germplasm.service';
import { GermplasmNameSettingModel } from '../../../../../shared/germplasm/model/germplasm-name-setting.model';
import { ModalConfirmComponent } from '../../../../../shared/modal/modal-confirm.component';
import { UrlService } from '../../../../../shared/service/url.service';
import { SubscriptionsManager } from '../../../../../shared/util/subscriptions-manager';
import { ParentModel } from '../../../../../shared/study/model/parent.model';
import { GermplasmDetailsUrlService } from '../../../../../shared/germplasm/service/germplasm-details.url.service';
import { CrossService } from '../../../../../shared/study/service/cross.service';
import { ImportCrossesContext } from '../import-crosses-context';
import { StudyEditorContext } from '../../../../study-editor.context';
import { StudyEditorService } from '../../../../study-editor.service';
import { GermplasmDetailsPopupService } from '../../../../../germplasm-details/germplasm-details-popup.service';
import { ExistingCrossesModalComponent } from '../../../../../design-crosses/existing-crosses.modal.component';
import { SaveTemplateComponent } from '../../../../../study-manager/advance/save-template.component';
import { ImportCrossesRequestModel } from '../../../../../shared/study/model/import-crosses.request.model';
import { TranslateService } from '@ngx-translate/core';
import { DesignCrossesContext } from '../../../../../design-crosses/design-crosses.context';
import { PreviewImportedCrossesModel } from '../../../../../shared/study/model/preview-imported-crosses.model';
import { MAX_NAME_DISPLAY_SIZE } from '../../../../../app.constants';

@Component({
    selector: 'jhi-crossing-settings',
    templateUrl: './import-crosses-settings-modal.component.html',
    styleUrl: './import-crosses-settings-modal.component.scss'
})
export class ImportCrossesSettingsModalComponent implements OnInit, OnDestroy {

    protected readonly PLOT_DUPE_PREFIX = 'Plot Dupe';
    protected readonly PEDIGREE_DUPE_PREFIX = 'Pedigree Dupe';
    protected readonly PLOT_RECIP_PREFIX = 'Plot Recip';
    protected readonly PEDIGREE_RECIP_PREFIX = 'Pedigree Recip';

    MAX_NAME_DISPLAY_SIZE = MAX_NAME_DISPLAY_SIZE;
    defaultSeparator = '/';
    BreedingMethodFilterTypeEnum = BreedingMethodFilterTypeEnum;
    isBreedingMethodScreen = true;
    isPreview = false;
    breedingMethodRadio = 1;
    applyGroupingToNewCrossesOnly = false;
    breedingMethodSelectedId: number;
    disableBreedingMethodSelect = true;
    showApplyGroupingCheckBox = false;
    namingRadio = 0;
    checkExistingCrosses = false;
    hasPlotDuplicates = false;
    estimatedHarvestDate: NgbDate;
    locationId: number;
    presetSettingId = 0;
    presetSettings: CrossNamePresetSetting[];
    numbers = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    originalList: PreviewImportedCrossesModel[] = [];
    completePreviewList: PreviewImportedCrossesModel[];
    listPerPage: PreviewImportedCrossesModel[][];
    currentPagePreviewList: PreviewImportedCrossesModel[];
    totalItems = 0;
    currentPageCount: number;
    page = 1;
    itemsPerPage = 10;
    isLoading = false;
    previousPage: number;
    plotDuplicateEntries = [];

    crossNameSetting: CrossNameSetting;

    omitAlertedCrosses = false;
    showRecordsWithAlerts = false;
    omitPlotDuplicates = false;
    hybridMethods = [];
    nextNameInSequence: string;
    sampleParentageDesignation: string;

    breedingMethodHelpLink: string;
    harvestLocationHelpLink: string;
    importedCrossesList : any;

    subscriptionManager = new SubscriptionsManager();
    @ViewChild(BreedingMethodsSelectComponent) breedingMethodsSelect: BreedingMethodsSelectComponent;

    constructor(private route: ActivatedRoute,
                private context: ImportCrossesContext,
                private crossService: CrossService,
                private alertService: AlertService,
                public activeModal: NgbActiveModal,
                private modalService: NgbModal,
                private paramContext: ParamContext,
                private helpService: HelpService,
                private dateHelperService: DateHelperService,
                private germplasmService: GermplasmService,
                private urlService: UrlService,
                private studyEditorContext: StudyEditorContext,
                private studyEditorService: StudyEditorService,
                private designCrossesContext: DesignCrossesContext,
                private translateService: TranslateService,
                protected germplasmDetailsPopupService: GermplasmDetailsPopupService,
                private germplasmDetailsUrlService: GermplasmDetailsUrlService) {
        this.paramContext.readParams();
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    ngOnInit() {
        const hybridMethodsPromise = this.crossService.getHybridMethods().toPromise();
        hybridMethodsPromise.then((hybridMethods) => {
            this.hybridMethods = hybridMethods;
        });
        this.showApplyGroupingCheckBox = this.context.listMetadata.hasHybridBreedingMethod;
        this.crossNameSetting = new CrossNameSetting(null, null, false, false, null, this.defaultSeparator, false, null);
        this.updateSampleParentageDesignation();
        this.loadPresets();

        if (!this.breedingMethodHelpLink || !this.breedingMethodHelpLink.length) {
            this.helpService.getHelpLink('MANAGE_CROP_BREEDING_METHODS').toPromise().then((response) => {
                this.breedingMethodHelpLink = response.body;
            });
        }

        if (!this.harvestLocationHelpLink || !this.harvestLocationHelpLink.length) {
            this.helpService.getHelpLink('MANAGE_CROP_BREEDING_LOCATIONS').toPromise().then((response) => {
                this.harvestLocationHelpLink = response.body;
            });
        }

        this.hasPlotDuplicates = this.context.listMetadata.hasPlotDuplicate;
    }

    close() {
        this.activeModal.dismiss('cancel');
    }

    goToNamingScreen() {
        this.isLoading = true;
        const useParentalStatus = this.breedingMethodRadio === 2;
        const basedOnImportFiles = this.breedingMethodRadio === 1;

        const importedDataList = this.context.listMetadata.importedCrossList;
        const icRequest: ImportCrossesRequestModel = new ImportCrossesRequestModel(this.breedingMethodSelectedId,
            importedDataList.some(data => data.femaleParents.length > 1),
            importedDataList.some(data => data.maleParents.length > 1),
            basedOnImportFiles, useParentalStatus,
            importedDataList.map(data => data.rawBreedingMethod), this.context.listMetadata.importedCrossList);
        this.subscriptionManager.add = this.crossService.validateBreedingMethodForImport(icRequest).subscribe((res: any) => {
            this.context.listMetadata.importedCrossList = res;
            this.proceedToNaming();
        },() => this.isLoading = false);
    }

    proceedToNaming() {
        this.isLoading = false;
        this.isBreedingMethodScreen = false;
        this.isPreview = false;
        this.completePreviewList = [];
        this.originalList = [];
    }

    goBackToBreedingMethodScreen() {
        this.isBreedingMethodScreen = true;
    }

    goToCrossesPreview() {
        this.isLoading = true;
        this.omitAlertedCrosses = false;
        this.showRecordsWithAlerts = false;

        this.subscriptionManager.add = this.crossService.addSeedSourceToPreviewCrosses(this.studyEditorContext.studyId, this.context.listMetadata.importedCrossList).subscribe((res: any) => {
            this.context.listMetadata.importedCrossList = res;
            this.completePreviewList = res.sort((a, b) => Number(a.entryNumber) > Number(b.entryNumber) ? 1 : -1);
            this.originalList.push(...this.completePreviewList);
            this.originalList.forEach((cross) => {
                if (!this.plotDuplicateEntries.includes(cross.entryNumber) && cross.duplicateOrderedMap[this.PLOT_DUPE_PREFIX]) {
                    this.plotDuplicateEntries.push(...cross.duplicateOrderedMap[this.PLOT_DUPE_PREFIX].split(', ').map(Number));
                }
            });
            this.processPagination();
            this.loadPage(1, true);
            this.isPreview = true;
            this.isLoading = false;
        },() => this.isLoading = false);

    }

    saveCrosses() {
        this.isLoading = true;
        const crosses = this.originalList.filter((previewCross) =>
            (!this.omitAlertedCrosses || !previewCross.hasExistingCrosses) && (!this.omitPlotDuplicates || !this.plotDuplicateEntries.includes(previewCross.entryNumber)));
        const harvestDate = this.dateHelperService.convertFormattedNgbDateToString(this.estimatedHarvestDate, '-');
        const request = new SaveCrossesRequestModel(null, harvestDate, this.locationId, !this.applyGroupingToNewCrossesOnly,
            this.namingRadio, this.crossNameSetting, true, crosses);
        this.subscriptionManager.add = this.crossService.saveCrosses(this.studyEditorContext.studyId, request)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe(
                (res: PreviewCrossesModel[]) => this.onSaveCrossesSuccess(res),
                () => this.isLoading = false);
    }

    onSaveCrossesSuccess(savedCrosses: PreviewCrossesModel[]) {
        this.isLoading = false;
        this.alertService.success('design-crosses.save.success');
        if (this.activeModal) {
            this.activeModal.close();
        }

        const germplasmListCreationModalRef = this.modalService.open(GermplasmListCreationComponent as Component,
            { modalDialogClass: 'modal-lg', backdrop: 'static' });
        germplasmListCreationModalRef.componentInstance.model.listType = this.context.listMetadata.listType;
        germplasmListCreationModalRef.componentInstance.model.description = this.context.listMetadata.listDesc;
        germplasmListCreationModalRef.componentInstance.model.listName = this.context.listMetadata.listName;
        germplasmListCreationModalRef.componentInstance.showListOwner = true;

        const dateString = this.context.listMetadata.listDate;
        if (dateString !== null || dateString !== '') {
            germplasmListCreationModalRef.componentInstance.creationDate = this.dateHelperService.convertFormattedDateStringToNgbDate(dateString, DateHelperService.YYYY_MM_DD_DASH_FORMAT);
        }

        const user = this.context.listMetadata.listOwner;
        germplasmListCreationModalRef.componentInstance.model.listOwner = user.firstName + ' ' + user.lastName;
        germplasmListCreationModalRef.componentInstance.listOwnerId = user.id;
        germplasmListCreationModalRef.componentInstance.entries = savedCrosses.map((cross) => {
            const entry: GermplasmListEntry = new GermplasmListEntry();
            entry.entryNo = cross.entryNumber;
            entry.gid = cross.gid;
            entry.groupName = cross.cross;
            entry.seedSource = cross.seedSource;
            return entry;
        });
        germplasmListCreationModalRef.result.then(() => {
            setTimeout(() => {
                // If activeModal exists, it means this modal is opened from within jhipster app. Not external iframe.
                if (this.activeModal) {
                    this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'crosses-and-selections');
                }
            }, 500);
        }, () => {
            // If activeModal exists, it means this modal is opened from within jhipster app. Not external iframe.
            if (this.activeModal) {
                this.studyEditorService.navigateToTab(this.studyEditorContext.studyId, 'crosses-and-selections');
            }
        });
    }

    viewExistingCrosses(previewCross: PreviewImportedCrossesModel) {
        this.designCrossesContext.cross = previewCross;
        this.modalService.open(ExistingCrossesModalComponent as Component, { size: 'lg', backdrop: 'static' })
            .result.then(() => {
            });
    }

    getDuplicatePrefixString(duplicatePrefix: string) {
        return 'study-editor.import-crosses-settings.' + duplicatePrefix.toLowerCase().replace(' ', '.') + '.prefix';
    }

    getNextNameSequence() {
        if (this.crossNameSetting.prefix) {
            const germplasmNameSetting = new GermplasmNameSettingModel(this.crossNameSetting.prefix, this.crossNameSetting.suffix,
                this.crossNameSetting.addSpaceBetweenPrefixAndCode, this.crossNameSetting.addSpaceBetweenSuffixAndCode,
                this.crossNameSetting.numOfDigits, this.crossNameSetting.startNumber);
            this.germplasmService.getNextNameInSequence(germplasmNameSetting).toPromise().then((response) => {
                if (response.body) {
                    this.nextNameInSequence = response.body;
                }
            });
        }
    }

    updateSampleParentageDesignation() {
        this.sampleParentageDesignation = 'FEMALE' + this.crossNameSetting.separator + 'MALE';
    }

    loadPage(page: number, forceReload = false) {
        if (page !== this.previousPage || forceReload) {
            this.previousPage = page;
            this.currentPagePreviewList = this.listPerPage[page - 1];
            const itemCount = this.currentPagePreviewList.length;
            this.currentPageCount = ((page - 1) * this.itemsPerPage) + itemCount;
        }
    }

    resetTable(): void {
        this.page = 1;
        this.previousPage = 1;
        this.listPerPage = [];
        this.processPagination();
        this.loadPage(1, true);
    }

    processPagination() {
        this.totalItems = this.completePreviewList.length;

        if (this.totalItems === 0) {
            this.listPerPage = [];
            this.listPerPage.push([]);
            return;
        }

        this.listPerPage = this.completePreviewList.reduce((resultArray, item, index) => {
            const pageIndex = Math.floor(index / this.itemsPerPage);

            if (!resultArray[pageIndex]) {
                resultArray[pageIndex] = []; // start a new page
            }

            resultArray[pageIndex].push(item);

            return resultArray;
        }, []);
    }

    updateDisplayedEntries() {
        if (this.omitAlertedCrosses) {
            this.showRecordsWithAlerts = false;
            this.completePreviewList = this.originalList.filter((previewCross) =>
                !previewCross.hasExistingCrosses && (!this.omitPlotDuplicates || !this.plotDuplicateEntries.includes(previewCross.entryNumber)));
        } else if (this.showRecordsWithAlerts) {
            this.completePreviewList = this.originalList.filter((previewCross) =>
                previewCross.hasExistingCrosses && (!this.omitPlotDuplicates || !this.plotDuplicateEntries.includes(previewCross.entryNumber)));
        } else {
            this.completePreviewList = this.originalList.filter((previewCross) =>
                (!this.omitPlotDuplicates || !this.plotDuplicateEntries.includes(previewCross.entryNumber)));
        }
        this.processPagination();
        this.loadPage(1, true);
    }

    isManualNaming() {
        return this.namingRadio === 2;
    }
    disableGoToCrossesPreview() {
        return !this.estimatedHarvestDate || !this.locationId || (this.isManualNaming() && (!this.crossNameSetting.prefix || this.crossNameSetting.prefix.length === 0));
    }

    saveCrossingNamePreset() {
        if (Number(this.presetSettingId) !== 0 ) {
            this.updatePreset();
        } else {
            const crossingNamePreset: CrossNamePresetSetting = new CrossNamePresetSetting();
            crossingNamePreset.toolId = 23;
            crossingNamePreset.toolSection = 'CROSSING_NAME_PRESET';
            crossingNamePreset.type = 'CrossNameSettingPreset';
            crossingNamePreset.programUUID = this.paramContext.programUUID;
            this.setPresetSettingValues(crossingNamePreset);
            const saveTemplateModalRef = this.modalService.open(SaveTemplateComponent as Component);
            saveTemplateModalRef.result.then((presetName) => {
                crossingNamePreset.name = presetName;
                this.subscriptionManager.add = this.crossService.addPreset(crossingNamePreset).subscribe((savedPreset ) => {
                    this.alertService.success('design-crosses.preset.save.success');
                    this.presetSettings.push(savedPreset);
                    this.presetSettingId = savedPreset.id;
                });
            });
        }
    }

    updatePreset() {
        const crossNamePresetSetting = this.presetSettings.filter((preset) => preset.id === Number(this.presetSettingId))[0];
        this.setPresetSettingValues(crossNamePresetSetting);
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.title = this.translateService.instant('design-crosses.preset.update.confirm.title');
        confirmModalRef.componentInstance.message = this.translateService.instant('design-crosses.preset.update.confirm.message', { param: crossNamePresetSetting.name });
        confirmModalRef.result.then(() => {
            this.subscriptionManager.add = this.crossService.updatePreset(crossNamePresetSetting).subscribe((res: void) => {
                this.alertService.success('design-crosses.preset.update.success');
            });
        });
    }

    setPresetSettingValues(crossingNamePreset) {
        crossingNamePreset.prefix = this.crossNameSetting.prefix;
        crossingNamePreset.suffix = this.crossNameSetting.suffix;
        crossingNamePreset.addSpaceBetweenSuffixAndCode = this.crossNameSetting.addSpaceBetweenSuffixAndCode;
        crossingNamePreset.addSpaceBetweenPrefixAndCode = this.crossNameSetting.addSpaceBetweenPrefixAndCode;
        crossingNamePreset.numOfDigits = this.crossNameSetting.numOfDigits;
        crossingNamePreset.separator = this.crossNameSetting.separator;
        crossingNamePreset.saveParentageDesignationAsAString = this.crossNameSetting.saveParentageDesignationAsAString;
        crossingNamePreset.startNumber = this.crossNameSetting.startNumber;
    }

    applySelectedSetting() {
        if (Number(this.presetSettingId) !== 0) {
            const crossingNamePreset = this.presetSettings.filter((preset) => preset.id === Number(this.presetSettingId))[0];
            this.crossNameSetting.prefix = crossingNamePreset.prefix;
            this.crossNameSetting.suffix = crossingNamePreset.suffix;
            this.crossNameSetting.addSpaceBetweenSuffixAndCode = crossingNamePreset.addSpaceBetweenSuffixAndCode;
            this.crossNameSetting.addSpaceBetweenPrefixAndCode = crossingNamePreset.addSpaceBetweenPrefixAndCode;
            this.crossNameSetting.numOfDigits = crossingNamePreset.numOfDigits;
            this.crossNameSetting.separator = crossingNamePreset.separator;
            this.crossNameSetting.saveParentageDesignationAsAString = crossingNamePreset.saveParentageDesignationAsAString;
            this.crossNameSetting.startNumber = crossingNamePreset.startNumber;
        }
    }

    deleteSelectedSetting() {
        const crossNamePresetSetting = this.presetSettings.filter((preset) => preset.id === Number(this.presetSettingId))[0];
        const confirmModalRef = this.modalService.open(ModalConfirmComponent as Component);
        confirmModalRef.componentInstance.title = this.translateService.instant('design-crosses.preset.delete.confirm.title');
        confirmModalRef.componentInstance.message = this.translateService.instant('design-crosses.preset.delete.confirm.message', { param: crossNamePresetSetting.name });
        confirmModalRef.result.then(() => {
            this.subscriptionManager.add = this.crossService.deletePreset(this.presetSettingId).subscribe(() => {
                this.alertService.success('design-crosses.preset.delete.success');
                this.loadPresets();
            });
        });
    }

    updateShowApplyGroupingCheckBoxValue() {
        this.showApplyGroupingCheckBox = (this.breedingMethodRadio === 3 && this.breedingMethodSelectedId && this.hybridMethods.includes(Number(this.breedingMethodSelectedId)))
                || (this.breedingMethodRadio === 1 && this.context.listMetadata.hasHybridBreedingMethod);

        this.disableBreedingMethodSelect = this.breedingMethodRadio !== 3;
        if (this.disableBreedingMethodSelect) {
            this.breedingMethodSelectedId = null;
            this.breedingMethodsSelect.clearSelected();
        }
    }

    getNames(list: ParentModel[]) {
        if (list) {
            return list.map((parentModel) => parentModel.designation);
        }
        return [];
    }

    private loadPresets() {
        this.isLoading = true;
        this.subscriptionManager.add = this.crossService.getAllPresets('CROSSING_NAME_PRESET').subscribe((presets) => {
            this.presetSettings = presets;
            this.presetSettingId = 0;
            this.isLoading = false;
        }, () => this.isLoading = false);
    }

    openGermplasmDetailsInTab(gid: number) {
        const url = this.germplasmDetailsUrlService.getUrlAsString(gid);
        window.open(url, '_blank');
    }

    protected readonly Object = Object;
}
